import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getSessionDetail,
  getSessionOrders,
  getSessionPortfolio,
  getSessionApiUsage,
  getSessionPnlAnalysis,
  closeSession,
  type Order,
  type PortfolioState,
  type ApiUsageMetrics,
  type PnlAnalysis,
  type SessionDetail,
} from '../api/client';

function formatDateTime(iso: string | null): string {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function formatCurrency(value: number): string {
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
}

function formatPercent(value: number): string {
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
}

function StatusBadge({ status }: { status: string }) {
  const classMap: Record<string, string> = {
    active: 'badge badge--green',
    paused: 'badge badge--yellow',
    completed: 'badge badge--gray',
  };
  return <span className={classMap[status] || 'badge'}>{status}</span>;
}

function OrderStatusBadge({ status }: { status: Order['status'] }) {
  const classMap: Record<string, string> = {
    filled: 'badge badge--green',
    pending: 'badge badge--yellow',
    cancelled: 'badge badge--gray',
    rejected: 'badge badge--red',
  };
  return <span className={classMap[status] || 'badge'}>{status}</span>;
}

function SessionInfoHeader({ session }: { session: SessionDetail }) {
  return (
    <div className="detail-header">
      <div className="detail-header-row">
        <div>
          <span className="detail-label">Agent ID:</span>
          <span className="detail-value cell-mono">{session.agentId || 'N/A'}</span>
        </div>
        <div>
          <span className="detail-label">Status:</span>
          <StatusBadge status={session.status} />
        </div>
        <div>
          <span className="detail-label">Simulated Time:</span>
          <span className="detail-value">{formatDateTime(session.simulatedTime)}</span>
        </div>
        <div>
          <span className="detail-label">Created:</span>
          <span className="detail-value">{formatDateTime(session.createdAt)}</span>
        </div>
      </div>
    </div>
  );
}

function PortfolioSummaryCard({ portfolio, session }: { portfolio: PortfolioState; session: SessionDetail }) {
  const totalReturnPercent = session.initialCash > 0
    ? ((portfolio.totalValue - session.initialCash) / session.initialCash) * 100
    : 0;

  return (
    <div className="card">
      <h3 className="card-title">Portfolio Summary</h3>
      <div className="stats-grid">
        <div className="stat-item">
          <span className="stat-label">Cash Balance</span>
          <span className="stat-value">{formatCurrency(portfolio.cashBalance)}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Total Value</span>
          <span className="stat-value">{formatCurrency(portfolio.totalValue)}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Realized P&L</span>
          <span className={`stat-value ${portfolio.realizedPnl >= 0 ? 'text-green' : 'text-red'}`}>
            {formatCurrency(portfolio.realizedPnl)}
          </span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Unrealized P&L</span>
          <span className={`stat-value ${portfolio.unrealizedPnl >= 0 ? 'text-green' : 'text-red'}`}>
            {formatCurrency(portfolio.unrealizedPnl)}
          </span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Total Return</span>
          <span className={`stat-value ${totalReturnPercent >= 0 ? 'text-green' : 'text-red'}`}>
            {formatPercent(totalReturnPercent)}
          </span>
        </div>
      </div>
    </div>
  );
}

function TpSlBadge({ order }: { order: Order }) {
  if (order.tpSlStatus === 'none') return <span className="text-muted">-</span>;
  if (order.tpSlStatus === 'triggered_tp') return <span className="badge badge--green">TP Hit</span>;
  if (order.tpSlStatus === 'triggered_sl') return <span className="badge badge--red">SL Hit</span>;
  if (order.tpSlStatus === 'cancelled') return <span className="badge badge--gray">Cancelled</span>;
  // active
  const parts: string[] = [];
  if (order.takeProfit) parts.push(`TP: ${formatCurrency(order.takeProfit)}`);
  if (order.stopLoss) parts.push(`SL: ${formatCurrency(order.stopLoss)}`);
  return <span className="badge badge--blue">{parts.join(' | ')}</span>;
}

function OrderHistoryTable({ orders }: { orders: Order[] }) {
  if (orders.length === 0) {
    return (
      <div className="card">
        <h3 className="card-title">Order History</h3>
        <div className="empty-state">No orders placed in this session.</div>
      </div>
    );
  }

  return (
    <div className="card">
      <h3 className="card-title">Order History</h3>
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Symbol</th>
              <th>Side</th>
              <th>Quantity</th>
              <th>Status</th>
              <th>Fill Price</th>
              <th>TP/SL</th>
              <th>Timestamp</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => (
              <tr key={order.id}>
                <td>{order.orderType}</td>
                <td className="cell-mono">{order.symbol}</td>
                <td>
                  <span className={order.side === 'buy' ? 'text-green' : 'text-red'}>
                    {order.side.toUpperCase()}
                  </span>
                </td>
                <td>{order.quantity}</td>
                <td><OrderStatusBadge status={order.status} /></td>
                <td>{order.fillPrice ? formatCurrency(order.fillPrice) : '-'}</td>
                <td><TpSlBadge order={order} /></td>
                <td>{formatDateTime(order.filledAt || order.placedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TradedSymbolsList({ orders }: { orders: Order[] }) {
  const symbols = [...new Set(orders.map((o) => o.symbol))];
  if (symbols.length === 0) return null;

  return (
    <div className="card">
      <h3 className="card-title">Traded Symbols</h3>
      <div className="symbols-list">
        {symbols.map((sym) => (
          <span key={sym} className="symbol-tag">{sym}</span>
        ))}
      </div>
    </div>
  );
}

function ApiUsageCard({ metrics }: { metrics: ApiUsageMetrics }) {
  return (
    <div className="card">
      <h3 className="card-title">API Usage</h3>
      <div className="stats-grid">
        <div className="stat-item">
          <span className="stat-label">Total Requests</span>
          <span className="stat-value">{metrics.totalRequests.toLocaleString()}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Time Series</span>
          <span className="stat-value">{metrics.categories.timeSeries}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Indicators</span>
          <span className="stat-value">{metrics.categories.indicators}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Trading</span>
          <span className="stat-value">{metrics.categories.trading}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Portfolio</span>
          <span className="stat-value">{metrics.categories.portfolio}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Market Data</span>
          <span className="stat-value">{metrics.categories.marketData}</span>
        </div>
      </div>
      {metrics.latestRequestAt && (
        <div className="card-footer">
          <span className="detail-label">Latest Request:</span>{' '}
          {formatDateTime(metrics.latestRequestAt)}
        </div>
      )}
    </div>
  );
}

function PnlAnalysisCard({ analysis, initialCash }: { analysis: PnlAnalysis; initialCash: number }) {
  if (analysis.totalTrades === 0) {
    return (
      <div className="card">
        <h3 className="card-title">P&L Analysis</h3>
        <div className="empty-state">
          <p>No trades executed in this session.</p>
          <p className="mt-8">
            Current Balance: <strong>{formatCurrency(initialCash + analysis.unrealizedPnl)}</strong>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <h3 className="card-title">P&L Analysis</h3>
      <div className="stats-grid">
        <div className="stat-item">
          <span className="stat-label">Total Return</span>
          <span className={`stat-value ${analysis.totalReturnPercent >= 0 ? 'text-green' : 'text-red'}`}>
            {formatPercent(analysis.totalReturnPercent)}
          </span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Total Trades</span>
          <span className="stat-value">{analysis.totalTrades}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Winning Trades</span>
          <span className="stat-value text-green">{analysis.winningTrades}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Losing Trades</span>
          <span className="stat-value text-red">{analysis.losingTrades}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Avg Trade Return</span>
          <span className={`stat-value ${analysis.avgTradeReturnPercent >= 0 ? 'text-green' : 'text-red'}`}>
            {formatPercent(analysis.avgTradeReturnPercent)}
          </span>
        </div>
      </div>
    </div>
  );
}

export function SessionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const sessionQuery = useQuery({
    queryKey: ['session', id],
    queryFn: () => getSessionDetail(id!),
    enabled: !!id,
  });

  const ordersQuery = useQuery({
    queryKey: ['session-orders', id],
    queryFn: () => getSessionOrders(id!),
    enabled: !!id,
  });

  const portfolioQuery = useQuery({
    queryKey: ['session-portfolio', id],
    queryFn: () => getSessionPortfolio(id!),
    enabled: !!id,
  });

  const apiUsageQuery = useQuery({
    queryKey: ['session-api-usage', id],
    queryFn: () => getSessionApiUsage(id!),
    enabled: !!id,
  });

  const pnlQuery = useQuery({
    queryKey: ['session-pnl', id],
    queryFn: () => getSessionPnlAnalysis(id!),
    enabled: !!id,
  });

  const session = sessionQuery.data?.data;
  const orders = ordersQuery.data?.data ?? [];
  const portfolio = portfolioQuery.data?.data;
  const apiUsage = apiUsageQuery.data?.data;
  const pnlAnalysis = pnlQuery.data?.data;

  const isLoading = sessionQuery.isLoading;
  const isError = sessionQuery.isError;

  const queryClient = useQueryClient();

  const closeSessionMutation = useMutation({
    mutationFn: () => closeSession(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['session', id] });
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
  });

  const handleCloseSession = () => {
    if (window.confirm('Are you sure you want to close this session? This action cannot be undone.')) {
      closeSessionMutation.mutate();
    }
  };

  return (
    <div className="page">
      <div className="page-header-row">
        <button className="btn btn--secondary" onClick={() => navigate('/sessions')}>
          &larr; Back to Sessions
        </button>
        <h2>Session Detail</h2>
        {session && session.status !== 'completed' && (
          <button
            className="btn btn--danger"
            onClick={handleCloseSession}
            disabled={closeSessionMutation.isPending}
          >
            {closeSessionMutation.isPending ? 'Closing...' : '⏹ Close Session'}
          </button>
        )}
      </div>

      {closeSessionMutation.isError && (
        <div className="error-state">
          Failed to close session: {(closeSessionMutation.error as Error)?.message ?? 'Unknown error'}
        </div>
      )}

      {isLoading && <div className="loading-state">Loading session details...</div>}

      {isError && (
        <div className="error-state">
          Failed to load session: {(sessionQuery.error as Error)?.message ?? 'Unknown error'}
        </div>
      )}

      {session && (
        <div className="detail-content">
          <SessionInfoHeader session={session} />

          {portfolio && <PortfolioSummaryCard portfolio={portfolio} session={session} />}

          {pnlAnalysis && (
            <PnlAnalysisCard analysis={pnlAnalysis} initialCash={session.initialCash} />
          )}

          {apiUsage && <ApiUsageCard metrics={apiUsage} />}

          <OrderHistoryTable orders={orders} />

          <TradedSymbolsList orders={orders} />
        </div>
      )}
    </div>
  );
}
